import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { FiSearch } from 'react-icons/fi'
import { toast } from 'react-toastify'

import calendar from '../../assets/images/calendar.svg'
import clock from '../../assets/images/clock.svg'
import removeIcon from '../../assets/images/close.svg'
import Button from '../Button/Button'
import Input from '../Input/Input'
import SearchLocation from '../SearchLocation/SearchLocation'
import style from './Booking.module.scss'
import { bookingSchema, bookingType, TRIP_TYPES, TripType } from './booking.schema'
import {
  addHoursToDate,
  formatDate,
  getDateAtHour,
  getTomorrowDate,
  parseDateFromString,
} from '../../utils/dates.util'

export interface BookingProps {
  defaultValues?: bookingType
  buttonText?: string
  sidebarUI?: boolean
  onSubmit?: (data: bookingType) => void
}

const minPickupDateTime = addHoursToDate(new Date(), 4)
// const minReturnDateTime = addHoursToDate(new Date(), 10)

const tomorrowDateAt10AM = getDateAtHour(getTomorrowDate(), 10)
const Booking = ({ defaultValues, buttonText, sidebarUI = false, onSubmit }: BookingProps) => {
  const navigate = useNavigate()
  const methods = useForm({
    resolver: yupResolver(bookingSchema),
    defaultValues: defaultValues
      ? defaultValues
      : {
          tripType: TRIP_TYPES.ONE_WAY,
          locations: [
            {
              value: '',
            },
            {
              value: '',
            },
          ],
          pickupDate: formatDate(tomorrowDateAt10AM, 'yyyy-MM-dd'),
          pickupTime: formatDate(tomorrowDateAt10AM, 'HH:mm'),
        },
  })
  const { handleSubmit, register, control, watch } = methods
  const tripType = watch('tripType') as TripType

  const { fields, remove } = useFieldArray<bookingType>({
    control,
    name: 'locations',
  })

  const removeIndex = (index: number) => {
    remove(index)
  }

  const formSubmit = (data: bookingType) => {
    // If onSubmit prop is provided, use it instead of default navigation
    if (onSubmit) {
      onSubmit(data)
      return
    }

    const pickupDateTime = parseDateFromString(
      data.pickupDate + ' ' + data.pickupTime,
      'yyyy-MM-dd HH:mm',
    )

    if (pickupDateTime < minPickupDateTime) {
      toast.error('Pickup date/time must be at least 4 hours from now')
      return
    }

    // Default behavior: navigate to list page
    let params = new URLSearchParams()
    data.locations.forEach(e => {
      params.append('locations', e.value)
    })
    params.append('tripType', data.tripType)
    params.append('pickupDate', data.pickupDate)
    params.append('pickupTime', data.pickupTime)

    if (data.tripType === TRIP_TYPES.RETURN && data.returnDate && data.returnTime) {
      params.append('returnDate', data.returnDate)
      params.append('returnTime', data.returnTime)
    }

    navigate({
      pathname: '/list',
      search: '?' + createSearchParams(params.toString()),
    })
  }

  return (
    <div id="booking" className={`${style.booking} ${sidebarUI && style.sidebarUI}`}>
      <div className={style.booking__outer}>
        <FormProvider {...methods}>
          <form className="container" onSubmit={handleSubmit(formSubmit)}>
            <div className={style.booking__form}>
              <h4>Quote & book</h4>

              {/* Trip Type Tabs */}
              <div className={style.booking__tabs}>
                <button
                  type="button"
                  className={`${style.booking__tab} ${tripType === TRIP_TYPES.ONE_WAY ? style.booking__tab_active : ''}`}
                  onClick={() => methods.setValue('tripType', TRIP_TYPES.ONE_WAY)}
                >
                  One-way
                </button>
                <button
                  type="button"
                  className={`${style.booking__tab} ${tripType === TRIP_TYPES.RETURN ? style.booking__tab_active : ''}`}
                  onClick={() => methods.setValue('tripType', TRIP_TYPES.RETURN)}
                >
                  Return
                </button>
              </div>

              <div className={`${style.booking__inner}`}>
                {fields.map((field, index) => {
                  return (
                    <div className={`${style.booking__wrap} flex-wrap align-center`} key={field.id}>
                      <div className={style.booking__inputWrap}>
                        <SearchLocation
                          name={`locations.${index}.value`}
                          defaultValue={defaultValues?.locations?.[index]?.value || ''}
                          placeholder={index === 0 ? 'Pick-up location' : 'Drop-off location'}
                          noOptionsMessage={
                            index === 0
                              ? 'Please enter pickup location'
                              : 'Please enter drop-off location'
                          }
                        />
                      </div>
                      {index !== 0 && index !== fields.length - 1 && (
                        <div className={style.booking__add} onClick={() => removeIndex(index)}>
                          <img src={removeIcon} alt="Remove" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Pickup Date/Time */}
              <div
                className={`${style.booking__inner} ${tripType === TRIP_TYPES.ONE_WAY ? style.booking__innerLast : style.booking__innerFirst}`}
              >
                <div className={style.booking__item}>
                  <label>
                    <span>Pick-up Date</span>
                    <Input
                      name="pickupDate"
                      type="date"
                      label="Pick-up"
                      register={register}
                      prefixIcon={calendar}
                      min={formatDate(minPickupDateTime, 'yyyy-MM-dd')}
                    />
                  </label>
                </div>

                <div className={style.booking__item}>
                  <label>
                    <span>Pick-up Time</span>
                    <Input
                      name="pickupTime"
                      type="time"
                      label="Pick-up"
                      register={register}
                      prefixIcon={clock}
                    />
                  </label>
                </div>

                {tripType === TRIP_TYPES.ONE_WAY && (
                  <div className={`${style.booking__submit}`}>
                    <Button
                      label={buttonText ? buttonText : 'Search'}
                      fullWidth={true}
                      type="submit"
                    >
                      <div className={style.booking__submit__icon}>{FiSearch({})}</div>
                    </Button>
                  </div>
                )}
              </div>

              {/* Return Date/Time - Only shown for return trips */}
              {tripType === TRIP_TYPES.RETURN && (
                <div className={`${style.booking__inner} ${style.booking__innerLast}`}>
                  <div className={style.booking__item}>
                    <label>
                      <span>Return Date</span>
                      <Input
                        name="returnDate"
                        type="date"
                        label="Return"
                        register={register}
                        prefixIcon={calendar}
                      />
                    </label>
                  </div>

                  <div className={style.booking__item}>
                    <label>
                      <span>Return Time</span>
                      <Input
                        name="returnTime"
                        type="time"
                        label="Return"
                        register={register}
                        prefixIcon={clock}
                      />
                    </label>
                  </div>

                  <div className={`${style.booking__submit}`}>
                    <Button
                      label={buttonText ? buttonText : 'Search'}
                      fullWidth={true}
                      type="submit"
                    >
                      <div className={style.booking__submit__icon}>{FiSearch({})}</div>
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <br />
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
export default Booking
